import { useState, useCallback } from 'react';
import {
  useSubcontractors,
  useCreateSubcontractor,
  useUpdateSubcontractor,
  useDeleteSubcontractor,
} from '../../../hooks/useSubcontractors';
import { useCountries } from '../../../hooks/useCountries';
import { useProvinces } from '../../../hooks/useProvinces';
import { useDepartments } from '../../../hooks/useDepartments';
import { useDistricts } from '../../../hooks/useDistricts';
import { ITEMS_PER_PAGE, INITIAL_SUBCONTRACTOR_STATE } from '../constants/subcontractorsConstants';
import { filterSubcontractors } from '../utils/subcontractorsUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

/**
 * Hook personalizado para gestionar el estado y lógica de la tabla de beneficiarios
 */
export const useSubcontractorsTable = () => {
  // Estado local
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(INITIAL_SUBCONTRACTOR_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  // Queries
  const { data: subcontractors = [], isLoading } = useSubcontractors();
  const { data: countries = [] } = useCountries();
  const { data: provinces = [] } = useProvinces();
  const { data: departments = [] } = useDepartments();
  const { data: districts = [] } = useDistricts();

  // Mutations
  const createMutation = useCreateSubcontractor();
  const updateMutation = useUpdateSubcontractor();
  const deleteMutation = useDeleteSubcontractor();

  const filterFn = useCallback((collection, term) => filterSubcontractors(collection, term), []);

  const {
    data: paginatedData,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    setPage,
  } = usePaginatedFilter({
    data: subcontractors,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn,
  });

  const handleOpenDialog = () => {
    setSelectedSubcontractor(INITIAL_SUBCONTRACTOR_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedSubcontractor((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setSelectedSubcontractor((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (editMode) {
      updateMutation.mutate(selectedSubcontractor);
    } else {
      createMutation.mutate(selectedSubcontractor);
    }
    handleCloseDialog();
  };

  const handleEdit = (subcontractor) => {
    setSelectedSubcontractor(subcontractor);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = (subcontractor) => {
    setEntityToDelete(subcontractor);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (entityToDelete) {
      deleteMutation.mutate(entityToDelete.uuid);
      setDeleteConfirmOpen(false);
      setEntityToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEntityToDelete(null);
  };

  return {
    // Datos
    data: paginatedData,
    isLoading,
    totalPages,
    totalCount,
    page,
    searchTerm,
    dialogOpen,
    editMode,
    selectedSubcontractor,
    deleteConfirmOpen,
    entityToDelete,
    countries,
    provinces,
    departments,
    districts,
    rowsPerPage,

    // Manejadores
    handleSearch,
    handlePageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleRowsPerPageChange,
  };
};
